/**
 * AdminLogger.js
 * ─────────────────────────────────────────────────────────────────────
 * Thin wrapper over the existing src/log/logger.js.
 *
 * WHY a wrapper, not a rewrite:
 *   • logger.js is used by ChatHistory / Room / webhooks — touching it
 *     would mean re-validating all of them.
 *   • The wrapper adds exactly one piece of stable metadata
 *     (module: "superadmin") to every entry so backend log queries
 *     filter admin lines cleanly without needing per-call discipline
 *     at every call site.
 *
 * WHY no separate transport (bearer instead of x-api-key):
 *   • Admin actions land on /api/v1/admin/* with the bearer token
 *     attached by AdminApiClient. The BACKEND already logs those
 *     structurally via structlog, correlated by X-Request-ID.
 *   • This client logger is for ADMIN-SIDE TELEMETRY: button clicks,
 *     render errors, UI state transitions. It doesn't need bearer
 *     auth; it goes through the same /logs endpoint as everything
 *     else and the request_id correlates it back to the action.
 *
 * Public API mirrors logger.js: info/warn/error/debug + an audit()
 * convenience that just tags the call as an audit event in the data
 * payload so the backend can split admin-telemetry from admin-audit
 * later if it wants.
 */

import { logger } from "../../../../log/logger";

const MODULE_TAG = { module: "superadmin" };

const stamp = (data) => ({ ...MODULE_TAG, ...(data || {}) });

export const adminLogger = {
  info:  (msg, data) => logger.info(msg,  stamp(data)),
  warn:  (msg, data) => logger.warn(msg,  stamp(data)),
  error: (msg, data) => logger.error(msg, stamp(data)),
  debug: (msg, data) => logger.debug(msg, stamp(data)),

  /**
   * Audit-style event. Use for actor-initiated actions that have a
   * backend-side audit log counterpart, so the frontend trace and the
   * backend audit row correlate via the action name.
   *
   *   adminLogger.audit("user_suspended", { userId: "u_abc" });
   *
   * The msg field becomes `audit:<action>` so log queries can pivot on
   * it without parsing the data blob.
   */
  audit: (action, data) =>
    logger.info(`audit:${action}`, stamp({ action, ...(data || {}) })),
};

export default adminLogger;
